import dayjs from "apps/visits/packages/shared/libs/dayjs";
import { useCallback, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { useEffectOnce } from "react-use";
import { IntersectionFadeIn } from "@/core/shared/v2/ui/intersection-fade-in";
import { SidebarLayout } from "@/core/shared/v2/ui/sidebar-layout";
import { useGetAvailableSlots } from "../api/get-available-slots.query";
import { Card, FlowSteps, PageHeader, SideCart } from "../components";
import { TimeSelectForm } from "../components/time-select-form";
import { useRoutes } from "../hooks/use-routes";
import { useCartStore } from "../store/use-cart-store";
import { useOrderingStore } from "../store/use-ordering-store";
import {
  addPatientVisitDateEvent,
  beginCheckoutEvent,
  goToUpsellEvent,
  safeTrackEvent,
  viewAvailableVisitDatesEvent,
} from "../tracking/events";
import { hasProductUpsellPackages } from "../utils/has-product-upsell-packages";

export const SelectTimeSlot = () => {
  const { t } = useTranslation();
  const { selectTimeSlot, timeSlot } = useOrderingStore();
  const { goToCatalog, goToLimitReached, goToPhoneVerification, goToUpsell } = useRoutes();
  const { products, getStandardProduct } = useCartStore();
  const standardProduct = getStandardProduct();
  const hasUpsell = hasProductUpsellPackages(standardProduct);

  const { data } = useGetAvailableSlots({
    config: {
      refetchOnWindowFocus: true,
    },
  });

  useEffect(() => {
    if (data?.availableSlots.length === 0) return goToLimitReached();
  }, [data, goToLimitReached]);

  useEffectOnce(() => {
    safeTrackEvent(() => viewAvailableVisitDatesEvent(), "view_available_visit_dates", "select-time-slot mount");
  });

  const handleTimeChange = useCallback(
    (time: string | undefined) => {
      // Keep the store in sync with the live selection. Clearing (e.g. after changing the day in the
      // calendar) must reset the stored slot so the CTA can't advance with a stale time.
      selectTimeSlot(time && time !== "DEFAULT" ? new Date(time) : undefined);
    },
    [selectTimeSlot],
  );

  const handleNextStep = () => {
    if (!timeSlot || !standardProduct) return;

    const slot = dayjs(timeSlot);
    safeTrackEvent(
      () =>
        addPatientVisitDateEvent({
          visitDate: slot.format("YYYY-MM-DD"),
          visitDateDay: slot.format("dddd"),
          visitDateHour: slot.format("HH:mm"),
        }),
      "add_patient_visit_date",
      "select-time-slot handleNextStep",
    );

    if (hasUpsell) {
      safeTrackEvent(() => goToUpsellEvent(), "go_to_upsell", "select-time-slot handleNextStep");
      goToUpsell();
      return;
    }

    // No upsell page in this flow — checkout begins as we move to phone-verification.
    if (products.length > 0) {
      const analyticsProducts = products.map((p) => ({
        ...p,
        productType: "blood-test-package" as const,
        quantity: 1,
        item_list_id: p.item_list_id ?? "listing",
      }));
      const totalPrice = products.reduce((acc, p) => acc + Number(p.grossPrice), 0);
      safeTrackEvent(
        () => beginCheckoutEvent(analyticsProducts, { value: totalPrice }, { numberOfProducts: products.length }),
        "begin_checkout",
        "select-time-slot direct phone-verification",
      );
    }
    goToPhoneVerification();
  };

  return (
    <IntersectionFadeIn className="mx-auto w-full max-w-[1275px] px-4 pt-8 pb-14 md:px-8 md:pb-20 lg:px-12">
      <FlowSteps />
      <PageHeader header={`${t("GROUP_ACTIONS_ORDERING.GENERAL.SELECT_TIME_SLOT")}`} onBack={goToCatalog} />
      <SidebarLayout>
        <Card>
          <div className="flex flex-col gap-y-6">
            <TimeSelectForm
              availableSlots={data?.availableSlots}
              onTimeChange={handleTimeChange}
              defaultTimeSlot={timeSlot}
            />
          </div>
        </Card>
        <SideCart onNextStep={handleNextStep} ctaDisabled={!timeSlot} />
      </SidebarLayout>
    </IntersectionFadeIn>
  );
};
